import React, { useState, useEffect, useRef } from 'react';
import { MAPBOX_TOKEN } from '../firebaseConfig';

const AddressModal = ({ onClose, onAddressSet }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [dropdownPos, setDropdownPos] = useState(null);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  const modalBodyRef = useRef(null);

  const searchAddress = async (input) => {
    console.log('[AddressModal] searchAddress called with input:', input, 'token exists:', !!MAPBOX_TOKEN);
    if (!input || input.length < 3) {
      console.log('[AddressModal] Input too short, skipping');
      return;
    }
    if (!MAPBOX_TOKEN) {
      console.error('[AddressModal] Mapbox token is not configured');
      return;
    }
    setLoading(true);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(input)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&types=address,place`;
      console.log('[AddressModal] Fetching:', url);
      const response = await fetch(url);
      const data = await response.json();
      console.log('[AddressModal] Mapbox response:', data);
      setSuggestions(data.features || []);
    } catch (error) {
      console.error('[AddressModal] Mapbox search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedPlace(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      searchAddress(value);
      updateDropdownPosition();
    }, 400);
  };

  // Update position on resize/scroll
  useEffect(() => {
    if (suggestions.length > 0) {
      updateDropdownPosition();
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, true);
      return () => {
        window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('scroll', updateDropdownPosition, true);
      };
    }
  }, [suggestions.length]);

  const handleSelect = (place) => {
    setQuery(place.place_name);
    setSelectedPlace(place);
    setSuggestions([]);
    setDropdownPos(null);
  };

  const handleConfirm = () => {
    if (!selectedPlace) return;
    const [lng, lat] = selectedPlace.center || [0, 0];
    const context = selectedPlace.context || [];
    let city = '';
    let state = '';
    let zip = '';
    context.forEach((item) => {
      if (item.id.startsWith('place.')) city = item.text;
      if (item.id.startsWith('region.')) state = item.text;
      if (item.id.startsWith('postcode.')) zip = item.text;
    });

    onAddressSet({
      address: selectedPlace.place_name,
      city,
      state,
      zip,
      lat,
      lng,
      source: 'manual',
    });
  };

  return (
    <div className="ff-modal-overlay" onClick={onClose}>
      <div className="ff-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ff-modal-header">
          <h3><i className="fas fa-map-marker-alt mr-2 text-orange"></i>Enter Your Address</h3>
          <button className="ff-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="ff-modal-body" ref={modalBodyRef}>
          <p className="text-muted mb-3">
            We need your address to show nearby restaurants. Your location data is not stored unless you create an account.
          </p>
          {!MAPBOX_TOKEN && (
            <div className="alert alert-warning mb-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              <strong>Mapbox token not configured.</strong> Add <code>REACT_APP_MAPBOX_TOKEN</code> to your environment and restart the dev server.
            </div>
          )}
          <div className="ff-form-group" style={{ position: 'relative' }}>
            <label>Search Address</label>
            <input
              ref={inputRef}
              type="text"
              className="ff-form-control"
              value={query}
              onChange={handleInputChange}
              placeholder="Start typing your address..."
              autoFocus
            />
            {loading && (
              <div style={{ position: 'absolute', right: 12, top: 42 }}>
                <div className="ff-spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
              </div>
            )}
          </div>
          {selectedPlace && (
            <div className="alert alert-info mt-3">
              <i className="fas fa-check-circle mr-2"></i>
              Selected: <strong>{selectedPlace.place_name}</strong>
            </div>
          )}
        </div>
        <div className="ff-modal-footer">
          <button className="ff-btn ff-btn-secondary ff-btn-block" onClick={onClose}>
            Cancel
          </button>
          <button
            className="ff-btn ff-btn-primary ff-btn-block"
            onClick={handleConfirm}
            disabled={!selectedPlace}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Suggestions dropdown rendered as fixed-position portal so it always sits on top */}
      {suggestions.length > 0 && dropdownPos && (
        <div
          className="ff-suggestions"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {suggestions.map((place, index) => (
            <div key={index} className="ff-suggestion-item" onClick={() => handleSelect(place)}>
              <i className="fas fa-map-marker-alt"></i>
              {place.place_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressModal;
